/**
 * DynamicTimeout - Calcula timeout de sincronização baseado no volume de dados
 *
 * Para fazendas grandes (ex: 5500+ matrizes), o timeout padrão de 5 minutos
 * é insuficiente. Este módulo calcula um timeout proporcional ao volume esperado.
 *
 * @module sync/DynamicTimeout
 */

import database from "../database";
import { Q } from "@nozbe/watermelondb";
import { syncLogger } from "./SyncLogger";

// Constantes de configuração
const BASE_TIMEOUT = 5 * 60 * 1000; // 5 minutos base
const MAX_TIMEOUT = 15 * 60 * 1000; // 15 minutos máximo
const TIMEOUT_PER_500_BATCHES = 60 * 1000; // +1 minuto por cada 500 lotes

/**
 * Calcula timeout dinâmico baseado no volume estimado de dados
 *
 * A estimativa é baseada no número de lotes (batches) das fazendas,
 * pois cada lote pode ter centenas de registros em d0s, dns e iatfs.
 *
 * @param {string[]} farmIds - IDs das fazendas a sincronizar
 * @returns {Promise<number>} Timeout em milissegundos
 *
 * @example
 * // Fazenda pequena (50 lotes): ~5 minutos
 * // Fazenda média (500 lotes): ~6 minutos
 * // Fazenda grande (2000 lotes): ~9 minutos
 * const timeout = await calculateDynamicTimeout(['farm-123']);
 */
export async function calculateDynamicTimeout(farmIds = []) {
  // Se não há fazendas específicas, usar timeout base
  if (!farmIds || farmIds.length === 0) {
    return BASE_TIMEOUT;
  }

  try {
    // Contar lotes nas fazendas selecionadas
    // Primeiro, buscar os corrals (retiros) das fazendas
    const corrals = await database.collections
      .get("corrals")
      .query(Q.where("farm_id", Q.oneOf(farmIds)))
      .fetch();

    const corralIds = corrals.map((c) => c.id);

    // Se não há corrals, usar timeout base
    if (corralIds.length === 0) {
      return BASE_TIMEOUT;
    }

    // Contar lotes nos corrals
    const batchCount = await database.collections
      .get("batches")
      .query(Q.where("corral_id", Q.oneOf(corralIds)))
      .fetchCount();

    // Calcular timeout extra baseado em lotes
    const extraTimeoutUnits = Math.ceil(batchCount / 500);
    const calculatedTimeout =
      BASE_TIMEOUT + extraTimeoutUnits * TIMEOUT_PER_500_BATCHES;

    // Limitar ao máximo
    const finalTimeout = Math.min(calculatedTimeout, MAX_TIMEOUT);

    // Log para diagnóstico
    syncLogger.log("DYNAMIC_TIMEOUT_CALCULATED", {
      farmIds,
      corralCount: corralIds.length,
      batchCount,
      baseTimeout: BASE_TIMEOUT,
      extraTimeout: extraTimeoutUnits * TIMEOUT_PER_500_BATCHES,
      finalTimeout,
      maxTimeout: MAX_TIMEOUT,
    });

    console.log(`[SYNC-TIMEOUT] Timeout calculado:`, {
      fazendas: farmIds.length,
      lotes: batchCount,
      timeout: `${Math.round(finalTimeout / 60000)} minutos`,
    });

    return finalTimeout;
  } catch (error) {
    // Em caso de erro, usar timeout base para não bloquear sync
    console.warn(
      "[SYNC-TIMEOUT] Erro ao calcular timeout dinâmico, usando base:",
      error
    );
    syncLogger.error("DYNAMIC_TIMEOUT_ERROR", error);
    return BASE_TIMEOUT;
  }
}

/**
 * Calcula timeout para retry com backoff progressivo
 *
 * A cada tentativa falha, o timeout aumenta em 50%.
 *
 * @param {number} baseTimeout - Timeout base calculado
 * @param {number} attempt - Número da tentativa (1, 2, 3...)
 * @returns {number} Timeout ajustado para a tentativa
 *
 * @example
 * // Tentativa 1: timeout base
 * // Tentativa 2: timeout * 1.5
 * // Tentativa 3: timeout * 2.25
 */
export function getRetryTimeout(baseTimeout, attempt) {
  const multiplier = Math.pow(1.5, attempt - 1);
  const retryTimeout = Math.min(baseTimeout * multiplier, MAX_TIMEOUT);

  console.log(`[SYNC-TIMEOUT] Retry #${attempt}:`, {
    baseTimeout: `${Math.round(baseTimeout / 60000)} min`,
    multiplier: multiplier.toFixed(2),
    retryTimeout: `${Math.round(retryTimeout / 60000)} min`,
  });

  return retryTimeout;
}

/**
 * Estima o tempo de download baseado no tamanho do payload
 *
 * Útil para mostrar progresso ao usuário.
 *
 * @param {number} estimatedSizeBytes - Tamanho estimado em bytes
 * @param {number} connectionSpeedKbps - Velocidade estimada em Kbps (default: 500 = 3G lento)
 * @returns {number} Tempo estimado em milissegundos
 */
export function estimateDownloadTime(
  estimatedSizeBytes,
  connectionSpeedKbps = 500
) {
  const bytesPerSecond = (connectionSpeedKbps * 1000) / 8;
  const estimatedSeconds = estimatedSizeBytes / bytesPerSecond;
  return estimatedSeconds * 1000;
}

/**
 * Constantes exportadas para uso externo
 */
export const TIMEOUT_CONSTANTS = {
  BASE_TIMEOUT,
  MAX_TIMEOUT,
  TIMEOUT_PER_500_BATCHES,
};
